//node_modules
import React, { useEffect, useRef, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

// primereact components
import { Card } from "primereact/card";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

// components
import Footer from "../../components/Footer";

// redux
import { useLoginMutation } from "../../redux/services/auth";
import { useLazyGetMeQuery } from "../../redux/services/user";
import { setLoading } from "../../redux/slices/app.slice";

// utils
import getSubdomain from "../../utils/getSubdomain";

// context
import { ToastContext } from "../../App";

//styles
import * as styles from "./Login.module.css";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showToast = useContext(ToastContext);

  const [
    login,
    { isSuccess: isLoginSuccess, isError: isLoginError, error: loginError },
  ] = useLoginMutation();
  const [
    getUser,
    {
      data: profile,
      isSuccess: isGetUserSuccess,
      isFetching: isGetUserFetching,
    },
  ] = useLazyGetMeQuery();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  }, []);

  useEffect(() => {
    if (isLoginError) {
      showToast("error", loginError?.status, loginError?.data?.message);
      dispatch(setLoading(false));
    }
  }, [isLoginError]);

  useEffect(() => {
    if (isLoginSuccess) {
      showToast("success", "Login", "Login was successful");
      dispatch(setLoading(false));
      getUser();
    }
  }, [isLoginSuccess]);

  useEffect(() => {
    if (isGetUserSuccess && !isGetUserFetching) {
      const role = profile.role;
      switch (role) {
        case "admin":
          navigate("/app/admin");
          break;
        case "manager":
          navigate("/app/manager");
          break;
        case "chef":
          navigate("/app/chef");
          break;
      }
    }
  }, [isGetUserSuccess, isGetUserFetching]);

  const onSubmit = (formData) => {
    login({
      email: formData.email,
      password: formData.password,
      subdomain: getSubdomain(),
    });
    dispatch(setLoading(true));
  };

  return (
    <>
      <div className={styles.wrapper}>
        <Card title="Sign In" className={styles.card}>
          <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
            <div className={styles.fieldContainer}>
              <label htmlFor="email" className="block mb-2">
                Email
              </label>
              <InputText
                id="email"
                {...register("email", { required: "Please enter your email" })}
                className={errors.email ? "p-invalid" : ""}
                placeholder="Your Email"
              />
              {errors.email && (
                <small className={`p-error ${styles.errorMessage}`}>
                  {errors.email.message}
                </small>
              )}
            </div>

            <div className={styles.fieldContainer}>
              <label htmlFor="password" className="block mb-2">
                Password
              </label>
              <Controller
                name="password"
                control={control}
                defaultValue=""
                rules={{ required: "Please enter your password" }}
                render={({ field, fieldState }) => (
                  <>
                    <Password
                      id="password"
                      {...field}
                      toggleMask
                      feedback={false}
                      className={fieldState.invalid ? "p-invalid" : ""}
                      placeholder="Your Password"
                    />
                    {fieldState.error && (
                      <small className={`p-error ${styles.errorMessage}`}>
                        {fieldState.error.message}
                      </small>
                    )}
                  </>
                )}
              />
            </div>

            <Button
              label="Sign In"
              type="submit"
              className={`${styles.submitButton} p-button-primary`}
            />
          </form>
        </Card>
        <Footer />
      </div>
    </>
  );
};

export default Login;
